import { PageVisitTracker } from "@/analytics/PageVisitTracker";
import api, { getLoginRes, setLoginRes } from "@/api";
import { setBrandColors } from "@/hooks/useBrandColors";
import { logoutUser } from "@/services/user";
import { RootState } from "@/store";
import { updateEnrolledStatus } from "@/store/userSlice";
import { jwtDecode } from "jwt-decode";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import AdditionalDataAlert from "../Banner/AdditionalDataAlert";
import LogoutWarningModal from "../Modals/LogoutWarningModal";
import Footer from "../Navigation/Footer";
import { IframeModal } from "../Navigation/IframeModal";
import Navbar from "../Navigation/Navbar";

interface Proptypes {
  children: React.ReactNode;
}

export const IDLE_TIMEOUT = 1000 * 60 * 10; // 10 minutes

export const IDLE_MODAL_SEEN_KEY = "idleModalSeen";

const isIdleModalSeen = (): boolean => {
  return localStorage.getItem(IDLE_MODAL_SEEN_KEY) === "true";
};

const setIdleModalSeen = (seen: boolean): void => {
  if (seen) {
    localStorage.setItem(IDLE_MODAL_SEEN_KEY, "true");
  } else {
    localStorage.removeItem(IDLE_MODAL_SEEN_KEY);
  }
};

const ContentWrapper: React.FC<Proptypes> = ({ children }) => {
  const [idleModalOpen, setIdleModalOpen] = React.useState(false);
  const [message, setMessage] = useState("");
  const [waitDuration] = useState(5);
  const [, setIsApiTimeout] = useState(false);
  const [, setRetry] = useState(false);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user);

  const isAuthenticated = (): boolean => !!user.id;

  const location = useLocation();

  // const hexToRgb = (hex?: string) => {
  //   if (!hex) return null;
  //   const sanitized = hex.replace("#", "");
  //   const full =
  //     sanitized.length === 3
  //       ? sanitized
  //           .split("")
  //           .map((c) => c + c)
  //           .join("")
  //       : sanitized;
  //   const int = parseInt(full, 16);
  //   return {
  //     r: (int >> 16) & 255,
  //     g: (int >> 8) & 255,
  //     b: int & 255,
  //   };
  // };

  // const lightenHex = (hex?: string, weight = 0.8) => {
  //   const rgb = hexToRgb(hex);
  //   if (!rgb) return defaultLight;
  //   const r = Math.round(rgb.r + (255 - rgb.r) * weight);
  //   const g = Math.round(rgb.g + (255 - rgb.g) * weight);
  //   const b = Math.round(rgb.b + (255 - rgb.b) * weight);
  //   return `rgb(${r}, ${g}, ${b})`;
  // };

  const handleOnIdle = () => {
    setMessage(t("logout_warning_modal.session_timeout_warning_message"));
    setIdleModalOpen(true);
  };

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: handleOnIdle,
    // debounce: 500,
  });

  const checkTokenExpiration = useCallback(() => {
    const tokenResString = getLoginRes();

    if (!tokenResString) {
      return;
    }
    const tokenRes = JSON.parse(tokenResString);

    const accessToken = tokenRes.accessToken;

    const { exp }: { exp: number } = jwtDecode(accessToken);

    const expirationTime = exp * 1000;

    if (!expirationTime) return;

    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    // Token expiring soon (5 minutes warning)
    if (timeUntilExpiration < waitDuration * 60 * 1000 && !isIdleModalSeen()) {
      // Mark as seen immediately so navigation or repeated checks don't
      // re-trigger the modal while we attempt a refresh.
      setIdleModalSeen(true);

      setMessage(t("logout_warning_modal.session_timeout_expired_message"));
      setIsApiTimeout(true);
      setIdleModalOpen(true);
      setRetry(true);

      api
        .post(`/UserAccount/Refresh`, getLoginRes(), {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          setLoginRes(res.data);
          window.dispatchEvent(
            new CustomEvent("re-authenticate", {
              detail: {
                accessToken: res.data.accessToken,
                refreshToken: res.data.refreshToken,
              },
            }),
          );
        })
        .catch(() => {
          logoutUser();
        });
      // Optionally refresh token here
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitDuration]);

  useEffect(() => {
    if (
      location.pathname === "/login" ||
      location.pathname.startsWith("/sep-introduction")
    ) {
      return;
    }

    // Check immediately
    checkTokenExpiration();

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [checkTokenExpiration, location.pathname]);

  const handleOpenIframeModal = () => {
    setIsIframeModalOpen(true);
  };

  const onAccountOpeningResult = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: any) => {
      const status = result?.status;

      if (typeof status === "string") {
        dispatch(updateEnrolledStatus(status));
      }
    },
    [dispatch],
  );

  const onStatusUpdate = useCallback(() => {
    if (status) dispatch(updateEnrolledStatus(status));
  }, [dispatch]);

  const handleCloseIframeModal = useCallback(() => {
    setIsIframeModalOpen(false);
  }, []);

  const getIframeSrc = (): string => {
    const tokenResString = getLoginRes();

    if (!tokenResString) {
      return "";
    }
    try {
      const tokenRes = JSON.parse(tokenResString);

      const accessToken = tokenRes.accessToken;

      const baseUrl = `${import.meta.env.VITE_APP_ADDITIONAL_DATA_BASE_URL}/#/alpaca`;

      return `${baseUrl}?accessToken=${accessToken}`;
    } catch {
      return "";
    }
  };

  useLayoutEffect(() => {
    if (window.partner) {
      const partner = window.partner;
      document.title = partner.title + " Employee Portal";
      setBrandColors({ brandColor: partner.colors.primary });
      const favicon = document.querySelector(
        'link[rel="icon"]',
      ) as HTMLLinkElement;

      if (favicon && partner.favicon) {
        favicon.href = partner.favicon;
      }
    }
  }, []);
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <PageVisitTracker />

      {idleModalOpen && isAuthenticated() && (
        <LogoutWarningModal
          isOpen={idleModalOpen}
          message={message}
          countdownSeconds={waitDuration * 60}
          okHandler={() => {
            setIdleModalOpen(false);
          }}
        />
      )}

      {/* Add Data Required Alert */}
      <AdditionalDataAlert onOpenIframe={handleOpenIframeModal} />

      {isIframeModalOpen && (
        <IframeModal
          open={isIframeModalOpen}
          onClose={handleCloseIframeModal}
          iframeSrc={getIframeSrc()}
          onStatusUpdate={onStatusUpdate}
          onAccountOpeningResult={onAccountOpeningResult}
        />
      )}

      <main
        style={{
          //  background: `linear-gradient(180deg, #EEFCFC 0%, #FFF 100%), linear-gradient(90deg, #EEFCFC 0%, #FFF 100%)`,
          background: `linear-gradient(180deg, var(--sootchy-bg-75) 0%, #FFF 100%), linear-gradient(90deg, var(--sootchy-bg-75) 0%, #FFF 100%)`,
          //background: `linear-gradient(180deg, #F9F9F9 0%, #FFF 100%), linear-gradient(180deg, #EEFCFC 0%, #FFF 100%), #D9D9D9`,
        }}
        className="flex-grow overflow-hidden"
      >
        <div>{children}</div>
      </main>
      <Footer />
    </div>
  );
};

export default ContentWrapper;
